import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import arrowLeftImage from '../../assets/icons/arrow_left.png';
import badgeImage from '../../assets/icons/worldcup/worldcup-badge.png';
import arrowRightIcon from '../../assets/icons/worldcup/worldcup-arrow-right.svg';
import arrowRightDisabledIcon from '../../assets/icons/worldcup/worldcup-arrow-right-disabled.svg';
import checkIcon from '../../assets/icons/worldcup/worldcup-check.svg';
import ticketIcon from '../../assets/icons/worldcup/worldcup-ticket.svg';
import panelArrowIcon from '../../assets/icons/worldcup/worldcup-panel-arrow.svg';
import swissFlagImage from '../../assets/flags/worldcup-swiss.png';
import canadaFlagImage from '../../assets/flags/worldcup-canada.png';
import titleBallImage from '../../assets/images/worldcup/worldcup-title-ball.png';
import topDecorImage from '../../assets/images/worldcup/worldcup-top-decor.png';
import bottomDecorImage from '../../assets/images/worldcup/worldcup-bottom-decor.png';
import { useToast } from '../../context/ToastContext';
import { useWorldcupEventPage } from './hooks/useWorldcupEventPage';

const FLAG_IMAGE_BY_KEY = {
  스위스: swissFlagImage,
  캐나다: canadaFlagImage,
  '/assets/flags/worldcup-swiss.png': swissFlagImage,
  '/assets/flags/worldcup-canada.png': canadaFlagImage,
};

const formatKickoffLabel = (kickoffAt) => {
  if (!kickoffAt) {
    return '-';
  }

  const date = new Date(kickoffAt);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return `${date.getMonth() + 1}월 ${date.getDate()}일 ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
};

const formatCompactKickoffLabel = (kickoffAt) => {
  if (!kickoffAt) {
    return '-';
  }

  const date = new Date(kickoffAt);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return `${date.getMonth() + 1}.${date.getDate()}. ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
};

const formatPercent = (value) => (value == null ? '-' : `${value}%`);
const formatPoint = (value) => `${Number(value ?? 0).toLocaleString()}P`;
const formatTicketCount = (value) => `${Number(value ?? 0).toLocaleString()}장`;
const formatHiddenTicketCount = (hidden) => (hidden ? '??장' : null);
const MODE_OPTIONS = [
  { value: 'mock', label: '목업' },
  { value: 'real', label: '실모드' },
];
const GRANT_SOURCE_ROUTE_BY_CODE = {
  punch: '/punch',
  timeNyang: '/time-nyang',
  mulNyang: '/mulnyang-bulnyang',
};

const formatPredictionSummary = (card) => `내 예측 ${formatTicketCount(card.myPrediction.ticketCount)}`;

const getPredictionSummaryPoint = (card) => {
  if (!card.myPrediction.hasPrediction || card.myPrediction.ticketCount <= 0) {
    return formatPoint(0);
  }

  if (Number(card.myPrediction.earnedPoint ?? 0) > 0) {
    return formatPoint(card.myPrediction.earnedPoint);
  }

  return formatPoint(0);
};

const getTabStateLabel = (tabStatus) => {
  if (tabStatus === 'active') {
    return '진행중';
  }

  if (tabStatus === 'completed') {
    return '종료';
  }

  return '오픈예정';
};

const resolveFlagImage = (team, mode = 'mock') => {
  if (mode === 'real') {
    return team.flagUrl ?? null;
  }

  return FLAG_IMAGE_BY_KEY[team.flagUrl] ?? FLAG_IMAGE_BY_KEY[team.name] ?? team.flagUrl ?? null;
};

const getTeamPanelState = (card, side) => {
  const isHome = side === 'home';
  const team = isHome ? card.homeTeam : card.awayTeam;
  const hasWinner = card.result.winnerTeamId != null;
  const isWinner = hasWinner && card.result.winnerTeamId === team.teamId;

  if (card.cardStatus === 'locked') {
    return { panelTone: 'muted' };
  }

  if (card.cardStatus === 'settled' && hasWinner) {
    if (card.myPrediction.result === 'won' && team.isMyPick) {
      return { panelTone: 'successBlue' };
    }

    if ((card.myPrediction.result === 'lost' || card.myPrediction.result === 'none') && isWinner) {
      return { panelTone: 'winnerGray' };
    }

    return { panelTone: 'muted' };
  }

  if (card.cardStatus === 'open' || card.cardStatus === 'closed') {
    return { panelTone: 'default' };
  }

  if (card.cardStatus === 'cancelled') {
    return { panelTone: 'muted' };
  }

  return { panelTone: 'default' };
};

function WorldcupEventTopBar() {
  const navigate = useNavigate();

  return (
    <header
      className="relative z-[2] flex h-[46px] items-center justify-between px-3 pl-[10px]"
      aria-label="상단 네비게이션"
    >
      <button
        type="button"
        className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
        onClick={() => navigate(-1)}
        aria-label="뒤로가기"
      >
        <img src={arrowLeftImage} alt="" className="h-6 w-6 object-contain brightness-0 invert" />
      </button>
      <div className="h-6 w-6" aria-hidden="true" />
    </header>
  );
}

function Badge({ children, tone, clickable = false, onClick }) {
  const toneClassMap = {
    progress: 'bg-[#EFECFF] text-[#8A6AD3]',
    success: 'bg-[#3B5CD2] text-white',
    fail: 'bg-[#F6475F] text-white',
    cancel: 'bg-[#D9D9D9] text-black',
    neutral: 'bg-[#D9D9D9] text-[#000000]',
    open: 'bg-[#03C75A] text-white',
    upcoming: 'bg-[#D9D9D9] text-[#5E5E5E]',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`rounded-[10px] border-0 px-[10px] py-[6px] text-[12px] font-bold leading-[1.4] ${toneClassMap[tone]} ${
        clickable ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      {children}
    </button>
  );
}

function TeamPanel({
  team,
  align = 'left',
  panelTone = 'default',
  predictionMode = false,
  hideRatio = false,
  draftSelected = false,
  predictionSelected = false,
  predictionUnselected = false,
  myPick = false,
  selectable = false,
  selected = false,
  onClick,
}) {
  const shouldUsePredictionDraftStyle = predictionMode && selected;
  const panelClass = draftSelected
    ? 'border border-solid border-[#D76144] bg-[#FFD5DB]'
    : shouldUsePredictionDraftStyle
      ? 'border border-solid border-[#D76144] bg-[#FFD5DB]'
      : predictionSelected
        ? 'border border-solid border-[#D76144] bg-[#FFD5DB]'
        : predictionUnselected
          ? 'bg-[#F4F4F4]'
          : selected
            ? 'border border-solid border-[#D76144] bg-[#FFD5DB]'
            : panelTone === 'successBlue'
              ? 'bg-[#374DF4]'
              : panelTone === 'winnerGray'
                ? 'bg-[#ABAAAA]'
                : panelTone === 'muted'
                  ? 'bg-[#F4F4F4]'
                  : 'bg-white';
  const textClass = panelTone === 'successBlue' || panelTone === 'winnerGray' ? 'text-white' : 'text-[#333333]';
  const isRight = align === 'right';
  const flagNode = team.flag ? (
    <img alt={team.name} src={team.flag} className="h-[24px] w-[30px] shrink-0 object-cover" />
  ) : (
    <div className="h-[36px] w-[36px] shrink-0 rounded-full bg-[#EAEAEA]" />
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!selectable}
      className={`relative h-[70px] w-full rounded-[10px] border-0 px-[10px] py-[16px] text-left ${panelClass} ${
        selectable ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      <div className="flex h-full items-center justify-between gap-3">
        {!isRight && flagNode}
        <div className="flex min-w-0 flex-col gap-[2px] text-left">
          <div className={`truncate text-[16px] font-bold leading-[1.3] ${textClass}`}>{team.name}</div>
          <div className={`whitespace-nowrap text-[18px] font-bold leading-[1.3] ${textClass}`}>
            {hideRatio ? '' : team.ratio}
          </div>
        </div>
        {isRight && flagNode}
      </div>
      {myPick && (
        <div className="absolute left-[8px] top-0 -translate-y-1/2 rounded-[999px] bg-[#F6475F] px-[5px] py-[1px] text-[8px] font-bold leading-[1.4] text-white">
          MY PICK
        </div>
      )}
    </button>
  );
}

function MatchCardCenter({ card }) {
  if (card.cardStatus === 'settled') {
    const hasScore = card.homeScore != null && card.awayScore != null;

    if (!hasScore) {
      return (
        <div className="relative flex w-[100px] flex-col items-center overflow-visible">
          <div className="w-full text-center text-[32px] font-black uppercase leading-[1.3] text-[#919191]">VS</div>
          <div className="mt-[6px] w-[94px] rounded-[10px] bg-[linear-gradient(180deg,#3B5CD2_0%,#8B68DD_100%)] py-[4px] text-center text-[12px] font-bold leading-[1.4] text-white">
            누적 {formatPoint(card.totalPoolPoint)}
          </div>
        </div>
      );
    }

    return (
      <div className="relative flex w-[100px] flex-col items-center overflow-visible">
        <div className="w-full text-center text-[32px] font-black leading-[1.3] text-[#333333]">
          {card.homeScore} : {card.awayScore}
        </div>
        <div className="mt-[6px] w-[94px] rounded-[10px] bg-[linear-gradient(180deg,#3B5CD2_0%,#8B68DD_100%)] py-[4px] text-center text-[12px] font-bold leading-[1.4] text-white">
          누적 {formatPoint(card.totalPoolPoint)}
        </div>
      </div>
    );
  }

  if (card.cardStatus === 'cancelled' || card.cardStatus === 'closed' || card.cardStatus === 'locked') {
    return (
      <div className="relative flex w-[100px] flex-col items-center overflow-visible">
        <div className="w-full text-center text-[32px] font-black uppercase leading-[1.3] text-[#919191]">VS</div>
        <div className="mt-[6px] w-[95px] rounded-[10px] bg-[#E2E6FF] py-[4px] text-center text-[12px] font-bold leading-[1.4] text-[#3B5CD2]">
          {card.cardStatus === 'locked' ? '오픈 예정' : formatCompactKickoffLabel(card.kickoffAt)}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex w-[100px] flex-col items-center overflow-visible">
      <div className="w-full text-center text-[32px] font-black uppercase leading-[1.3] text-[#919191]">VS</div>
      <div className="mt-[6px] w-[94px] rounded-[10px] bg-[linear-gradient(180deg,#3B5CD2_0%,#8B68DD_100%)] py-[4px] text-center text-[12px] font-bold leading-[1.4] text-white">
        누적 {formatPoint(card.totalPoolPoint)}
      </div>
    </div>
  );
}

function MatchCardBottom({ card, draft, maxTicketCount, isExpanded, onTicketChange, onSubmitPrediction }) {
  if (
    card.cardStatus === 'settled' ||
    card.cardStatus === 'cancelled' ||
    (card.cardStatus === 'closed' && card.myPrediction.hasPrediction)
  ) {
    return (
      <div className="px-[10px] pb-4 pt-3">
        <div className="flex h-[38px] w-full items-center justify-center rounded-[12px] border border-solid border-[#D9D9D9] bg-[#F4F4F4] px-[16px] text-[14px] leading-[1.4]">
          <span className="font-normal text-[#5E5E5E]">{formatPredictionSummary(card)}</span>
          <span className="ml-[5px] font-normal text-[#3B5CD2]">{getPredictionSummaryPoint(card)}</span>
        </div>
      </div>
    );
  }

  if (card.cardStatus === 'open' && !card.myPrediction.hasPrediction) {
    const hasTickets = maxTicketCount > 0;
    const hasSelectedTeam = Boolean(draft.selectedTeamId);
    const isCtaEnabled = hasTickets && draft.ticketCount >= 1 && draft.ticketCount <= maxTicketCount;
    const canSubmit = isCtaEnabled && hasSelectedTeam;

    if (!isExpanded) return null;

    return (
      <div className="mt-[15px] w-full rounded-b-[20px] border border-solid border-[#D9D9D9] bg-[#F4F4F4] px-5 pb-[16px] pt-[15px]">
        <div className="flex h-[26px] w-full items-center justify-center gap-[5px] bg-[#E2E6FF] py-[3px] text-[14px] font-bold leading-[1.4] text-[#3B5CD2]">
          <span>예측할 팀을 탭하세요</span>
          <img alt="" src={panelArrowIcon} className="h-[10px] w-[10px]" />
        </div>
        <div className="mt-[15px] flex flex-wrap items-center justify-center gap-[10px]">
          <div className="px-[10px] py-[5px] text-[14px] font-normal leading-[1.4] text-[#5E5E5E]">티켓을 정하세요</div>
          <button
            type="button"
            onClick={() => onTicketChange(-1)}
            disabled={draft.ticketCount <= 0}
            className="rounded-[3px] border border-solid border-[rgba(0,0,0,0.12)] bg-[#D9D9D9] px-[10px] py-[5px] text-[14px] leading-[1.4] text-[#333333] disabled:opacity-40"
          >
            -
          </button>
          <div className="text-[14px] font-normal leading-[1.4] text-[#333333]">{draft.ticketCount}장</div>
          <button
            type="button"
            onClick={() => onTicketChange(1)}
            disabled={draft.ticketCount >= maxTicketCount}
            className="rounded-[3px] border border-solid border-[rgba(0,0,0,0.12)] bg-[#D9D9D9] px-[10px] py-[5px] text-[14px] leading-[1.4] text-[#333333] disabled:opacity-40"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => {
              if (canSubmit) {
                onSubmitPrediction();
              }
            }}
            disabled={!isCtaEnabled}
            className={`flex h-[26px] min-w-[58px] items-center justify-center rounded-[8px] border-0 px-[8px] text-[12px] font-normal leading-[1.4] ${
              isCtaEnabled ? 'bg-[#000000] text-white' : 'bg-[#D9D9D9] text-[#5E5E5E]'
            }`}
          >
            예측하기
          </button>
        </div>
        <div className="mt-[15px] text-center text-[12px] font-normal leading-[1.4] text-[#919191]">
          <p className="font-bold text-red-500">한 번 건 티켓은 변경·취소 불가</p>
          <p>※ 포인트 분배 시 소수점은 제외</p>
        </div>
      </div>
    );
  }

  if (card.cardStatus === 'open' && card.myPrediction.hasPrediction) {
    return (
      <div className="px-[10px] pb-4 pt-3">
        <div className="flex h-[38px] w-full items-center justify-center rounded-[12px] border border-solid border-[#D9D9D9] bg-[#F4F4F4] px-[16px] text-[14px] leading-[1.4]">
          <span className="font-normal text-[#5E5E5E]">{formatPredictionSummary(card)}</span>
          <span className="ml-[5px] font-normal text-[#3B5CD2]">{getPredictionSummaryPoint(card)}</span>
        </div>
      </div>
    );
  }

  return null;
}

function MatchCard({
  mode,
  card,
  draft,
  maxTicketCount,
  isExpanded,
  onTogglePredictionPanel,
  onSelectTeam,
  onTicketChange,
  onSubmitPrediction,
}) {
  const contentPaddingBottomClass =
    card.cardStatus === 'closed' ||
    card.cardStatus === 'cancelled' ||
    card.cardStatus === 'locked' ||
    card.cardStatus === 'settled' ||
    (card.cardStatus === 'open' && !isExpanded)
      ? 'pb-[16px]'
      : '';
  const homePanelState = getTeamPanelState(card, 'home');
  const awayPanelState = getTeamPanelState(card, 'away');
  const isPredictionPanelOpen = card.cardStatus === 'open' && !card.myPrediction.hasPrediction && isExpanded;
  const canTriggerPrediction = card.cardStatus === 'open' && !card.myPrediction.hasPrediction;
  const isSelectable = canTriggerPrediction;
  const hasDraftSelection = Boolean(draft.selectedTeamId) && !card.myPrediction.hasPrediction && isExpanded;
  const hasConfirmedPrediction = card.myPrediction.hasPrediction;
  const homeDraftSelected = hasDraftSelection && card.homeTeam.teamId === draft.selectedTeamId;
  const awayDraftSelected = hasDraftSelection && card.awayTeam.teamId === draft.selectedTeamId;
  const homePredictionSelected =
    hasConfirmedPrediction && card.homeTeam.isMyPick && (card.cardStatus === 'open' || card.cardStatus === 'closed');
  const awayPredictionSelected =
    hasConfirmedPrediction && card.awayTeam.isMyPick && (card.cardStatus === 'open' || card.cardStatus === 'closed');
  const homePredictionUnselected =
    hasConfirmedPrediction &&
    !homeDraftSelected &&
    !homePredictionSelected &&
    (card.cardStatus === 'open' || card.cardStatus === 'closed');
  const awayPredictionUnselected =
    hasConfirmedPrediction &&
    !awayDraftSelected &&
    !awayPredictionSelected &&
    (card.cardStatus === 'open' || card.cardStatus === 'closed');
  const pointPerTicketLabel =
    Number(card.myPrediction.earnedPoint ?? 0) > 0
      ? formatPoint(card.cardStatus === 'settled' ? card.result.pointPerTicket : card.estimatedPayoutPerTicket)
      : null;
  const selectedSide =
    card.myPrediction.pickedTeamId == null
      ? card.cardStatus === 'settled' && card.result.winnerTeamId != null
        ? card.result.winnerTeamId === card.awayTeam.teamId
          ? 'away'
          : 'home'
        : draft.selectedTeamId === card.awayTeam.teamId
          ? 'away'
          : 'home'
      : card.myPrediction.pickedTeamId === card.awayTeam.teamId
        ? 'away'
        : 'home';
  const showPointWithHome =
    selectedSide === 'home' && (card.myPrediction.hasPrediction || (Boolean(draft.selectedTeamId) && isExpanded));
  const showPointWithAway =
    selectedSide === 'away' && (card.myPrediction.hasPrediction || (Boolean(draft.selectedTeamId) && isExpanded));
  const showMyPickOnHome = card.homeTeam.isMyPick && card.cardStatus !== 'cancelled';
  const showMyPickOnAway = card.awayTeam.isMyPick && card.cardStatus !== 'cancelled';
  const isPredictionMode = isPredictionPanelOpen;
  const hideRatioUntilPredicted =
    (card.cardStatus === 'open' || card.cardStatus === 'closed') && !card.myPrediction.hasPrediction;
  const hiddenTicketCountLabel = formatHiddenTicketCount(hideRatioUntilPredicted);
  const handleTeamPanelClick = (teamId) => {
    if (!canTriggerPrediction) {
      return;
    }

    if (!isExpanded) {
      onTogglePredictionPanel();
      onSelectTeam(teamId);
      return;
    }

    onSelectTeam(teamId);
  };

  return (
    <div className={`w-full rounded-[20px] bg-white ${card.cardStatus === 'locked' ? 'opacity-70' : ''}`}>
      <div className={`px-[10px] pt-[16px] ${contentPaddingBottomClass}`}>
        <div className="flex items-center justify-between px-[10px]">
          <div className="text-[16px] font-bold leading-[1.4] text-[#5E5E5E]">{formatKickoffLabel(card.kickoffAt)}</div>
          <Badge
            tone={
              card.cardStatus === 'open' && !card.myPrediction.hasPrediction && isExpanded ? 'neutral' : card.badge.tone
            }
            clickable={card.cardStatus === 'open' && !card.myPrediction.hasPrediction}
            onClick={
              card.cardStatus === 'open' && !card.myPrediction.hasPrediction ? onTogglePredictionPanel : undefined
            }
          >
            {card.cardStatus === 'open' && !card.myPrediction.hasPrediction && isExpanded ? '취소' : card.badge.label}
          </Badge>
        </div>

        <div className="mt-[14px] grid grid-cols-3 items-center gap-[4px]">
          <TeamPanel
            team={{
              name: card.homeTeam.name,
              ratio: formatPercent(card.homeTeam.pickRate),
              flag: resolveFlagImage(card.homeTeam, mode),
            }}
            predictionMode={isPredictionMode}
            hideRatio={hideRatioUntilPredicted}
            draftSelected={homeDraftSelected}
            predictionSelected={homePredictionSelected}
            predictionUnselected={homePredictionUnselected}
            selectable={isSelectable}
            selected={draft.selectedTeamId === card.homeTeam.teamId}
            onClick={() => handleTeamPanelClick(card.homeTeam.teamId)}
            panelTone={homePanelState.panelTone}
            myPick={showMyPickOnHome}
          />
          <div className="flex w-full shrink-0 justify-center">
            <MatchCardCenter card={card} />
          </div>
          <TeamPanel
            team={{
              name: card.awayTeam.name,
              ratio: formatPercent(card.awayTeam.pickRate),
              flag: resolveFlagImage(card.awayTeam, mode),
            }}
            predictionMode={isPredictionMode}
            hideRatio={hideRatioUntilPredicted}
            draftSelected={awayDraftSelected}
            predictionSelected={awayPredictionSelected}
            predictionUnselected={awayPredictionUnselected}
            selectable={isSelectable}
            selected={draft.selectedTeamId === card.awayTeam.teamId}
            onClick={() => handleTeamPanelClick(card.awayTeam.teamId)}
            align="right"
            panelTone={awayPanelState.panelTone}
            myPick={showMyPickOnAway}
          />
        </div>

        <div className="mt-[16px] flex items-center justify-between gap-[10px] px-[10px]">
          <div className="flex min-w-0 items-center gap-[6px]">
            <img alt="" src={ticketIcon} className="h-[14px] w-[18px]" />
            <span className="whitespace-nowrap text-[14px] font-normal leading-[1.4] text-[#5E5E5E]">
              {hiddenTicketCountLabel ?? formatTicketCount(card.homeTeam.ticketSum)}
            </span>
            {showPointWithHome && pointPerTicketLabel && (
              <span className="whitespace-nowrap text-[14px] font-normal leading-[1.4] text-[#3B5CD2]">
                1장당 {pointPerTicketLabel}
              </span>
            )}
          </div>
          <div className="flex min-w-0 items-center gap-[6px]">
            <img alt="" src={ticketIcon} className="h-[14px] w-[18px]" />
            <span className="whitespace-nowrap text-[14px] font-normal leading-[1.4] text-[#5E5E5E]">
              {hiddenTicketCountLabel ?? formatTicketCount(card.awayTeam.ticketSum)}
            </span>
            {showPointWithAway && pointPerTicketLabel && (
              <span className="whitespace-nowrap text-[14px] font-normal leading-[1.4] text-[#3B5CD2]">
                1장당 {pointPerTicketLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      <MatchCardBottom
        card={card}
        draft={draft}
        maxTicketCount={maxTicketCount}
        isExpanded={isExpanded}
        onTicketChange={onTicketChange}
        onSubmitPrediction={onSubmitPrediction}
      />
    </div>
  );
}

function DecorativeTop() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 h-[105px] overflow-hidden">
      <img src={topDecorImage} alt="" className="h-full w-full object-cover object-top" />
    </div>
  );
}

function DecorativeBottom() {
  return (
    <div className="pointer-events-none absolute inset-x-0 -bottom-[10px] h-[194px] overflow-hidden">
      <img src={bottomDecorImage} alt="" className="h-full w-full object-bottom" />
    </div>
  );
}

function DashedDivider({ className = '', lineClassName = 'w-24' }) {
  return (
    <div className={`flex justify-center ${className}`}>
      <div
        className={`h-px ${lineClassName} bg-[repeating-linear-gradient(to_right,#D9D9D9_0_4px,transparent_4px_7px)]`}
      />
    </div>
  );
}

export default function WorldcupEventPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showErrorToast } = useToast();
  const {
    allowMockMode,
    mode,
    setMode,
    event,
    tabs,
    ticketSummary,
    selectedRoundCode,
    setSelectedRoundCode,
    selectedRoundSection,
    expandedMatchId,
    togglePredictionPanel,
    getPredictionDraft,
    getMaxTicketCount,
    selectPredictionTeam,
    adjustPredictionTicketCount,
    submitPrediction,
  } = useWorldcupEventPage();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const errorCode = searchParams.get('error');

    if (!errorCode) {
      return;
    }

    showErrorToast(`월드컵 테스트 에러 토스트 (${errorCode})`);
  }, [location.search, showErrorToast]);

  const currentRoundLabel = useMemo(() => {
    return tabs.find((tab) => tab.roundCode === selectedRoundCode)?.label ?? tabs[0]?.label ?? '-';
  }, [selectedRoundCode, tabs]);

  return (
    <div className="relative min-h-full w-full overflow-hidden bg-black">
      <DecorativeTop />
      <DecorativeBottom />

      <div className="relative z-[1] w-full pb-[220px]">
        <div className="mx-auto w-full">
          <WorldcupEventTopBar />

          <div className="relative -top-6 flex items-start justify-end gap-[8px] px-[10px]">
            {allowMockMode ? (
              <div className="mt-[6px] flex h-[30px] items-center rounded-[999px] bg-[rgba(255,255,255,0.14)] p-[2px] backdrop-blur-sm">
                {MODE_OPTIONS.map((option) => {
                  const isSelected = option.value === mode;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setMode(option.value)}
                      className={`flex h-[26px] min-w-[58px] items-center justify-center rounded-[999px] border-0 px-[12px] text-[12px] font-bold leading-[1.4] ${
                        isSelected ? 'bg-white text-[#333333]' : 'bg-transparent text-white'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <img alt="야옹상회 월드컵 배지" src={badgeImage} className="h-[61px] w-[61px]" />
          </div>

          <div className="mt-[20px] px-[10px]">
            <div className="text-center">
              <div className="text-[28px] font-black leading-[1.4] text-white">야옹상회</div>
              <div className="text-[28px] font-black leading-[1.4] text-[#B696FF]">{event.title}</div>
            </div>

            <div
              className="mt-[16px] grid justify-between gap-[6px]"
              style={{ gridTemplateColumns: `repeat(${Math.max(tabs.length, 1)}, minmax(0, 1fr))` }}
            >
              {tabs.map((tab) => {
                const className = `flex aspect-square w-full min-w-0 flex-col items-center justify-center rounded-br-[10px] rounded-tl-[10px] rounded-tr-[10px] border ${
                  tab.isSelected
                    ? 'border-transparent bg-[linear-gradient(180deg,#3B5CD2_0%,#8B68DD_100%)] text-white'
                    : 'border-[#D9D9D9] bg-[#F4F4F4] text-[#919191]'
                }`;

                if (tab.tabStatus === 'upcoming') {
                  return (
                    <div key={tab.roundCode} className={className}>
                      <div className="text-[12px] font-bold leading-[1.4]">{getTabStateLabel(tab.tabStatus)}</div>
                      <div className="text-center text-[18px] font-bold leading-[1.4]">{tab.label}</div>
                    </div>
                  );
                }

                return (
                  <button
                    key={tab.roundCode}
                    type="button"
                    onClick={() => setSelectedRoundCode(tab.roundCode)}
                    className={className}
                  >
                    <div className="text-[12px] font-bold leading-[1.4]">{getTabStateLabel(tab.tabStatus)}</div>
                    <div className="text-center text-[18px] font-bold leading-[1.4]">{tab.label}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-[28px] flex flex-col items-center">
              <div className="flex items-center gap-[8px]">
                <div className="ml-7 text-[50px] font-extrabold leading-[1.4] text-white">{currentRoundLabel}</div>
                <img alt="" src={titleBallImage} className="h-[55px] w-[87px]" />
              </div>
              <div className="text-[14px] font-bold leading-[1.4] text-[#D9D9D9]">
                이벤트기간 : {event.periodLabel || '-'}
              </div>
            </div>

            <div className="relative mt-[28px] h-[240px] w-full">
              <div className="absolute inset-0 rounded-[20px] bg-white" />

              <div className="absolute inset-y-0 left-0 right-[37px] min-w-0 rounded-[20px] bg-white pb-[14px] pt-[14px]">
                <div className="pr-[71px]">
                  <div className="flex items-center justify-between px-[20px]">
                    <div className="justify-center text-center text-xl font-bold leading-7 text-black">
                      내 보유 티켓
                    </div>
                    <div className="text-right justify-center text-xl font-bold leading-7 text-[#3B5CD2]">
                      {ticketSummary.ticketBalance}장
                    </div>
                  </div>
                  <div className="mt-[10px] h-px w-full bg-[#D9D9D9]" />

                  <div className="mt-[11px] flex flex-col gap-[12px] px-[10px]">
                    {ticketSummary.guideLines.map((guide) => (
                      <div key={guide} className="flex items-start gap-[5px]">
                        <img alt="" src={checkIcon} className="mt-[1px] h-[13px] w-[14px]" />
                        <div className="whitespace-pre-line text-[12px] font-normal leading-[1.4] text-[#5E5E5E]">
                          {guide}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="absolute right-0 top-0 flex h-[240px] w-[108px] flex-col rounded-[20px] border border-solid border-[#D9D9D9] bg-[linear-gradient(180deg,#3B5CD2_0%,#8B68DD_100%)] pb-[11px] pt-[16px] shadow-[-3px_0_0_rgba(0,0,0,0.25)]">
                <div className="px-[8px] pb-[10px] text-center text-[18px] font-bold leading-[1.4] text-white">
                  <span className="block">티켓얻기</span>
                </div>
                <DashedDivider className="px-[6px]" />
                <div className="mt-[11px] flex flex-col gap-[6px] px-[8px]">
                  {ticketSummary.grantSources.map((game) => {
                    const routePath = game.routePath ?? GRANT_SOURCE_ROUTE_BY_CODE[game.gameCode] ?? null;

                    return (
                      <button
                        key={game.gameCode}
                        type="button"
                        disabled={game.disabled || !routePath}
                        onClick={() => {
                          if (!game.disabled && routePath) {
                            navigate(routePath);
                          }
                        }}
                        className="flex h-[35px] w-full items-center justify-between rounded-[10px] border-0 bg-white px-[10px] text-left"
                      >
                        <span
                          className={`text-[14px] font-bold leading-normal tracking-[-0.5px] ${
                            game.disabled ? 'text-[#D9D9D9]' : 'text-[#333333]'
                          }`}
                        >
                          {game.label}
                        </span>
                        <img
                          alt=""
                          src={game.disabled ? arrowRightDisabledIcon : arrowRightIcon}
                          className="h-4 w-4 shrink-0"
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-[8px] flex flex-col gap-[8px]">
              {(selectedRoundSection?.matches ?? []).map((card, index) => (
                <div key={card.matchId}>
                  {index > 0 && <DashedDivider className="mb-[8px] px-[5px]" lineClassName="w-full" />}
                  <MatchCard
                    mode={mode}
                    card={card}
                    draft={getPredictionDraft(card.matchId)}
                    maxTicketCount={getMaxTicketCount(card)}
                    isExpanded={expandedMatchId === card.matchId}
                    onTogglePredictionPanel={() => togglePredictionPanel(card.matchId)}
                    onSelectTeam={(teamId) => selectPredictionTeam(card.matchId, teamId)}
                    onTicketChange={(delta) => adjustPredictionTicketCount(card.matchId, delta)}
                    onSubmitPrediction={() => submitPrediction(card.matchId)}
                  />
                </div>
              ))}
              {selectedRoundSection?.matches?.length === 0 && (
                <div className="rounded-[20px] bg-white px-[20px] py-[28px] text-center text-[14px] text-[#5E5E5E]">
                  현재 노출할 경기가 없습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
